import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle
} from 'react'
import { FrameConfig } from '@renderer/types/configState'
import QRCode from 'qrcode'
import { useConfigStore } from '@renderer/stores/configState'
import { resolveGallerySessionUrl } from '@renderer/utils/galleryUrlUtils'
import { removeBackground } from '@renderer/utils/backgroundRemovalUtils'
import { clipPlaceholderAperture } from '@frameShapes'
import type { RawSlotMeta } from '@frameShapes'

const PHOTO_LOAD_TIMEOUT_MS = 5000
const PHOTO_LOAD_MAX_RETRIES = 3
const PHOTO_LOAD_RETRY_DELAYS = [300, 800, 1500] // ms backoff per retry

export interface PhotoState {
  url: string
  scale: number
  offsetX: number
  offsetY: number
  width: number
  height: number
  naturalWidth: number
  naturalHeight: number
  isMirrored?: boolean
  /** True if the image failed to load (e.g. timeout or network error). Slot will render as placeholder. */
  loadFailed?: boolean
}

interface PhotoStateMap {
  [key: string]: PhotoState
}

interface PrintPreviewProps {
  frameConfig: FrameConfig
  selectedPhotos: string[]
  className?: string
  isInteractive?: boolean // New prop to control interactions
  sessionId: string
}

export interface ExportResult {
  blob: Blob
  /** True if all assets were verified present before rendering */
  assetsVerified: boolean
  /** Non-empty when some assets were missing but we rendered anyway */
  degradedReason?: string
}

export interface PrintPreviewRef {
  exportImage: (timeoutMs?: number) => Promise<Blob>
  /**
   * Export with full status: tries strict validation first.
   * If strict=false (degraded), renders whatever is loaded (may be partial/blank)
   * so the session can still continue with upload + print.
   */
  exportImageWithStatus: (opts?: { timeoutMs?: number; strict?: boolean }) => Promise<ExportResult>
  getPhotoStates: () => Record<string, PhotoState>
  isReady: () => boolean
  waitForReady: (timeoutMs?: number) => Promise<void>
}

const PrintPreview = forwardRef<PrintPreviewRef, PrintPreviewProps>(
  ({ frameConfig, selectedPhotos, className = '', isInteractive = true, sessionId }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const photoStatesRef = useRef<PhotoStateMap>({})
    const [activePhoto, setActivePhoto] = useState<string | null>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [qrImage, setQrImage] = useState<ImageBitmap | null>(null)
    const [staticImages, setStaticImages] = useState<Map<string, ImageBitmap>>(new Map())
    const [renderKey, setRenderKey] = useState(0)
    const lastTouchRef = useRef<{ x: number; y: number } | null>(null)
    const lastPinchDistanceRef = useRef<number | null>(null)
    const resultPageSettings = useConfigStore((s) => s.config?.pages?.result?.settings)

    // Loading state management
    const [isLoading, setIsLoading] = useState(true)
    const isLoadingRef = useRef(true)

    // Mapping from placeholder index to unique identifier
    const placeholderIndexToKey = (index: number): string => `placeholder-${index}`

    // Find photo placeholder at position and return its unique identifier
    const findPhotoAtPosition = (x: number, y: number): string | null => {
      for (let i = 0; i < frameConfig.drawParams.images.length; i++) {
        const imageConfig = frameConfig.drawParams.images[i]
        if (imageConfig.type.startsWith('photo-')) {
          if (
            x >= imageConfig.left &&
            x <= imageConfig.left + imageConfig.width &&
            y >= imageConfig.top &&
            y <= imageConfig.top + imageConfig.height
          ) {
            return placeholderIndexToKey(i)
          }
        }
      }
      return null
    }

    const findPhotoAtPositionRef = useRef(findPhotoAtPosition)

    // Interaction state
    const getEventPosition = (
      e: React.MouseEvent | React.TouchEvent
    ): { x: number; y: number } | null => {
      const canvas = canvasRef.current
      if (!canvas) return null

      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height

      if ('touches' in e) {
        if (e.touches.length === 0) return null
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY
        }
      } else {
        return {
          x: (e.clientX - rect.left) * scaleX,
          y: (e.clientY - rect.top) * scaleY
        }
      }
    }

    const handleDragStart = (e: React.MouseEvent | React.TouchEvent): void => {
      // Only allow interaction if explicitly set to interactive
      if (!isInteractive) return

      const pos = getEventPosition(e)
      if (!pos) return

      const placeholderKey = findPhotoAtPosition(pos.x, pos.y)
      if (placeholderKey) {
        setActivePhoto(placeholderKey)
        setIsDragging(true)
        lastTouchRef.current = pos
      }
    }

    // Drag move handler
    const handleDragMove = (e: React.MouseEvent | React.TouchEvent): void => {
      // Prevent interaction if not interactive or not dragging
      if (!isInteractive || !isDragging || !activePhoto) return

      // Extract placeholder index from unique key (placeholder-${index})
      const placeholderIndex = parseInt(activePhoto.replace('placeholder-', ''), 10)
      if (isNaN(placeholderIndex) || placeholderIndex < 0 || placeholderIndex >= frameConfig.drawParams.images.length) {
        return
      }
      const frameImage = frameConfig.drawParams.images[placeholderIndex]

      // Handle multi-touch (pinch/zoom)
      if ('touches' in e && e.touches.length === 2) {
        const touch1 = e.touches[0]
        const touch2 = e.touches[1]
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        )

        if (lastPinchDistanceRef.current !== null) {
          const photo = photoStatesRef.current[activePhoto]

          if (photo && frameImage) {
            const pinchDelta = distance - lastPinchDistanceRef.current
            const SCALE_SENSITIVITY = 0.005
            const newScale = Math.max(
              0.1,
              Math.min(5, photo.scale * (1 + pinchDelta * SCALE_SENSITIVITY))
            )

            updatePhotoState(activePhoto, { scale: newScale })
          }
        }

        lastPinchDistanceRef.current = distance
        return
      }

      // Regular drag
      const pos = getEventPosition(e)
      if (!pos || !lastTouchRef.current) return

      const deltaX = pos.x - lastTouchRef.current.x
      const deltaY = pos.y - lastTouchRef.current.y

      const photo = photoStatesRef.current[activePhoto]

      if (photo && frameImage) {
        const scaledWidth = photo.naturalWidth * photo.scale
        const scaledHeight = photo.naturalHeight * photo.scale

        // For mirrored images, reverse deltaX
        const adjustedDeltaX = photo.isMirrored ? -deltaX : deltaX

        // Calculate effective frame dimensions based on rotation for clamping offsets
        let effectiveFrameWidth = frameImage.width
        let effectiveFrameHeight = frameImage.height
        const rotationDegrees = ((frameImage.rotation || 0) * 180) / Math.PI
        if (
          Math.abs(rotationDegrees - 90) < 1 ||
          Math.abs(rotationDegrees - 270) < 1 ||
          Math.abs(rotationDegrees + 90) < 1
        ) {
          effectiveFrameWidth = frameImage.height
          effectiveFrameHeight = frameImage.width
        }

        const maxOffsetX = (scaledWidth - effectiveFrameWidth) / 2
        const maxOffsetY = (scaledHeight - effectiveFrameHeight) / 2

        const newOffsetX = Math.max(
          -maxOffsetX,
          Math.min(maxOffsetX, photo.offsetX + adjustedDeltaX)
        )
        const newOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, photo.offsetY + deltaY))

        updatePhotoState(activePhoto, {
          offsetX: newOffsetX,
          offsetY: newOffsetY
        })
      }

      lastTouchRef.current = pos
    }

    // Drag end handler
    const handleDragEnd = (): void => {
      setIsDragging(false)
      setActivePhoto(null)
      lastTouchRef.current = null
      lastPinchDistanceRef.current = null
    }

    // Generate QR code
    useEffect(() => {
      const generateQR = async (): Promise<void> => {
        try {
          const galleryTarget = resolveGallerySessionUrl(sessionId, resultPageSettings)
          const qrDataUrl = await QRCode.toDataURL(galleryTarget, {
            margin: 2,
            width: 300
          })
          const response = await fetch(qrDataUrl)
          const blob = await response.blob()
          const imageBitmap = await createImageBitmap(blob)
          setQrImage(imageBitmap)
        } catch (error) {
          console.error('Failed to generate QR code:', error)
        }
      }
      generateQR()
    }, [sessionId, resultPageSettings?.galleryPublicBaseUrl])

    // Load static frame overlay images with multi-level fallback and AbortController
    const STATIC_LOAD_RETRY_DELAYS = [300, 600] as const
    const MAX_RETRIES_PER_LEVEL = 2

    useEffect(() => {
      const controller = new AbortController()
      const { signal } = controller

      const loadStaticImages = async (): Promise<void> => {
        const newImages = new Map<string, ImageBitmap>()

        const imagesToLoad = frameConfig.drawParams.images.filter(
          (imageConfig) =>
            imageConfig.type === 'image' && imageConfig.src && !staticImages.has(imageConfig.src)
        )

        if (imagesToLoad.length === 0) return

        const tryBlobToBitmap = (blob: Blob): Promise<ImageBitmap | null> =>
          createImageBitmap(blob).then((bitmap) =>
            bitmap.width > 0 && bitmap.height > 0 ? bitmap : null
          )

        const evictOnInvalid = (originalUrl: string): void => {
          try {
            useConfigStore.getState().evictAsset(originalUrl)
            import('@renderer/utils/assetCacheDB').then(({ deleteCachedBlob }) =>
              deleteCachedBlob(originalUrl).catch(() => {})
            )
          } catch {
            // ignore
          }
        }

        for (const imageConfig of imagesToLoad) {
          if (imageConfig.type !== 'image' || !imageConfig.src) continue
          if (signal.aborted) return

          const src = imageConfig.src
          const originalUrl = imageConfig.originalSrc ?? src
          let bitmap: ImageBitmap | null = null

          // Level 0: current src (blob URL) - fetch and decode
          if (src.startsWith('blob:')) {
            for (let r = 0; r <= MAX_RETRIES_PER_LEVEL && !bitmap && !signal.aborted; r++) {
              if (r > 0) await new Promise((res) => setTimeout(res, STATIC_LOAD_RETRY_DELAYS[r - 1]))
              try {
                const response = await fetch(src, { signal })
                if (!response.ok) continue
                const blob = await response.blob()
                if (blob.size === 0) continue
                bitmap = await tryBlobToBitmap(blob)
                if (!bitmap) evictOnInvalid(originalUrl)
              } catch {
                // blob URL dead or aborted, escalate
              }
            }
          }

          // Level 1: IndexedDB by original URL
          if (!bitmap && !signal.aborted) {
            for (let r = 0; r <= MAX_RETRIES_PER_LEVEL && !bitmap && !signal.aborted; r++) {
              if (r > 0) await new Promise((res) => setTimeout(res, STATIC_LOAD_RETRY_DELAYS[r - 1]))
              try {
                const { getCachedBlob } = await import('@renderer/utils/assetCacheDB')
                const blob = await getCachedBlob(originalUrl)
                if (!blob || blob.size === 0) continue
                const blobUrl = URL.createObjectURL(blob)
                try {
                  const res = await fetch(blobUrl, { signal })
                  if (!res.ok) continue
                  const b = await res.blob()
                  bitmap = await tryBlobToBitmap(b)
                  if (!bitmap) evictOnInvalid(originalUrl)
                } finally {
                  URL.revokeObjectURL(blobUrl)
                }
              } catch {
                // ignore
              }
            }
          }

          // Level 2: network fetch original URL
          if (!bitmap && !signal.aborted) {
            const fetchUrl =
              originalUrl.startsWith('/') && typeof window !== 'undefined'
                ? window.location.origin + originalUrl
                : originalUrl
            for (let r = 0; r <= MAX_RETRIES_PER_LEVEL && !bitmap && !signal.aborted; r++) {
              if (r > 0) await new Promise((res) => setTimeout(res, STATIC_LOAD_RETRY_DELAYS[r - 1]))
              try {
                const response = await fetch(fetchUrl, {
                  signal,
                  mode: originalUrl.startsWith('/') ? 'same-origin' : 'cors',
                  cache: 'no-cache'
                })
                if (!response.ok) continue
                const blob = await response.blob()
                if (blob.size === 0) continue
                bitmap = await tryBlobToBitmap(blob)
              } catch {
                // ignore
              }
            }
          }

          // Level 3: cache-busted network fetch
          if (!bitmap && !signal.aborted) {
            const fetchUrl =
              originalUrl.startsWith('/') && typeof window !== 'undefined'
                ? window.location.origin + originalUrl
                : originalUrl
            const bustedUrl =
              fetchUrl + (fetchUrl.includes('?') ? '&' : '?') + `_t=${Date.now()}`
            for (let r = 0; r <= MAX_RETRIES_PER_LEVEL && !bitmap && !signal.aborted; r++) {
              if (r > 0) await new Promise((res) => setTimeout(res, STATIC_LOAD_RETRY_DELAYS[r - 1]))
              try {
                const response = await fetch(bustedUrl, {
                  signal,
                  mode: originalUrl.startsWith('/') ? 'same-origin' : 'cors',
                  cache: 'reload'
                })
                if (!response.ok) continue
                const blob = await response.blob()
                if (blob.size === 0) continue
                bitmap = await tryBlobToBitmap(blob)
              } catch {
                // ignore
              }
            }
          }

          if (bitmap && !signal.aborted) {
            newImages.set(src, bitmap)
          }
        }

        if (signal.aborted) return
        setStaticImages((prev) => new Map([...prev, ...newImages]))
      }

      loadStaticImages()
      return (): void => controller.abort()
    }, [frameConfig.drawParams.images])

    // Cache for photo images
    const photoImagesCache = useRef<Map<string, HTMLImageElement>>(new Map())
    // Cache for background-removed photos (keyed by placeholder key)
    const bgRemovedImagesCache = useRef<Map<string, HTMLImageElement>>(new Map())

    // Check if all resources are loaded
    const checkLoadingComplete = useCallback(() => {
      // Check if all selected photos are loaded
      const allPhotosLoaded = selectedPhotos.every((photoUrl) => {
        if (!photoUrl) return true
        const actualUrl = photoUrl?.startsWith('mirror:') ? photoUrl.slice(7) : photoUrl
        return photoImagesCache.current.has(actualUrl)
      })

      // Check if QR code is loaded (if needed)
      const qrNeeded = frameConfig.drawParams.images.some((img) => img.type === 'qr')
      const qrLoaded = !qrNeeded || qrImage

      // Check if static images are loaded
      const staticImagesNeeded = frameConfig.drawParams.images.filter(
        (img) => img.type === 'image' && img.src
      )
      const staticImagesLoaded = staticImagesNeeded.every((img) =>
        img.src ? staticImages.has(img.src) : true
      )

      return allPhotosLoaded && qrLoaded && staticImagesLoaded
    }, [selectedPhotos, frameConfig.drawParams.images, qrImage, staticImages])

    // Load and cache photos
    useEffect(() => {
      const loadImages = async (): Promise<void> => {
        const newPhotoStates: PhotoStateMap = {}
        const currentPhotos = new Set(
          selectedPhotos.map((url) => (url?.startsWith('mirror:') ? url.slice(7) : url))
        )

        // Clean up cached images that are no longer selected
        const cachedUrls = Array.from(photoImagesCache.current.keys())
        cachedUrls.forEach((url) => {
          if (!currentPhotos.has(url)) {
            photoImagesCache.current.delete(url)
          }
        })

        // Create a mapping from photoType to selectedPhotos index
        const photoTypeToSelectedIndex: Record<string, number> = {}
        for (let i = 0; i < selectedPhotos.length; i++) {
          const photoType = `photo-${i + 1}`
          photoTypeToSelectedIndex[photoType] = i
        }

        // Clear bg-removed cache for placeholders that no longer apply
        const currentBgRemovedKeys = new Set<string>()
        for (let idx = 0; idx < frameConfig.drawParams.images.length; idx++) {
          const imageConfig = frameConfig.drawParams.images[idx]
          if (imageConfig.type.startsWith('photo-') && imageConfig.bgRemoved) {
            const selectedIndex = photoTypeToSelectedIndex[imageConfig.type]
            if (selectedIndex !== undefined && selectedPhotos[selectedIndex]) {
              currentBgRemovedKeys.add(placeholderIndexToKey(idx))
            }
          }
        }
        Array.from(bgRemovedImagesCache.current.keys()).forEach((key) => {
          if (!currentBgRemovedKeys.has(key)) {
            bgRemovedImagesCache.current.delete(key)
          }
        })

        // Iterate through all placeholders in frame config
        for (let placeholderIndex = 0; placeholderIndex < frameConfig.drawParams.images.length; placeholderIndex++) {
          const imageConfig = frameConfig.drawParams.images[placeholderIndex]
          
          // Only process photo placeholders
          if (!imageConfig.type.startsWith('photo-')) continue

          const photoType = imageConfig.type
          const selectedIndex = photoTypeToSelectedIndex[photoType]
          
          // Skip if no photo is assigned to this type
          if (selectedIndex === undefined || !selectedPhotos[selectedIndex]) continue

          const photoUrl = selectedPhotos[selectedIndex]
          const isMirrored = photoUrl?.startsWith('mirror:')
          const actualUrl = isMirrored ? photoUrl.slice(7) : photoUrl

          if (!actualUrl) continue

          const placeholderKey = placeholderIndexToKey(placeholderIndex)

          // Check if we have an existing state for this specific placeholder
          const existingState = photoStatesRef.current[placeholderKey]

          // Load and cache the image with retries (handles network, CORS, stale cache)
          let img: HTMLImageElement | null = null
          if (photoImagesCache.current.has(actualUrl)) {
            img = photoImagesCache.current.get(actualUrl)!
          } else {
            for (let attempt = 0; attempt < PHOTO_LOAD_MAX_RETRIES; attempt++) {
              if (attempt > 0) {
                await new Promise((r) => setTimeout(r, PHOTO_LOAD_RETRY_DELAYS[attempt - 1]))
                console.info(`[PrintPreview] Photo load retry ${attempt + 1}/${PHOTO_LOAD_MAX_RETRIES}:`, actualUrl.slice(0, 80))
              }
              let loadedImage: HTMLImageElement | null = null
              const loaded = await new Promise<boolean>((resolve) => {
                const image = new Image()
                loadedImage = image
                if (attempt > 0) image.crossOrigin = 'anonymous'
                const timeoutId = setTimeout(() => {
                  console.warn(`[PrintPreview] Photo load timeout (attempt ${attempt + 1}):`, actualUrl.slice(0, 80))
                  resolve(false)
                }, PHOTO_LOAD_TIMEOUT_MS)
                image.onload = (): void => {
                  clearTimeout(timeoutId)
                  resolve(true)
                }
                image.onerror = (): void => {
                  clearTimeout(timeoutId)
                  console.warn(`[PrintPreview] Photo load error (attempt ${attempt + 1}):`, actualUrl.slice(0, 80))
                  resolve(false)
                }
                // On retry, bust any cached response by appending a cache-buster
                const urlToLoad = attempt > 0 && !actualUrl.startsWith('blob:') && !actualUrl.startsWith('data:')
                  ? `${actualUrl}${actualUrl.includes('?') ? '&' : '?'}_r=${attempt}`
                  : actualUrl
                image.src = urlToLoad
              })
              const imageElement = loadedImage as HTMLImageElement | null
              if (loaded && imageElement?.complete && imageElement.naturalWidth > 0) {
                img = imageElement
                photoImagesCache.current.set(actualUrl, img)
                break
              }
            }
          }

          if (!img) {
            newPhotoStates[placeholderKey] = {
              url: actualUrl,
              scale: 1,
              offsetX: 0,
              offsetY: 0,
              width: imageConfig.width,
              height: imageConfig.height,
              naturalWidth: imageConfig.width,
              naturalHeight: imageConfig.height,
              isMirrored,
              loadFailed: true
            }
            continue
          }

          if (existingState && existingState.url === actualUrl && existingState.isMirrored === isMirrored && !existingState.loadFailed) {
            // Preserve the existing state's scale and offsets for this specific placeholder
            newPhotoStates[placeholderKey] = {
              ...existingState,
              width: imageConfig.width,
              height: imageConfig.height,
              isMirrored
            }
          } else {
            // Initial scale: always cover (fill slot; customer can pinch/zoom on select)
            const slotW = imageConfig.width
            const slotH = imageConfig.height
            const photoAspect = img.width / img.height
            const slotAspect = slotW / slotH
            let scale = 1
            if (photoAspect > slotAspect) {
              scale = slotH / img.height
            } else {
              scale = slotW / img.width
            }

            newPhotoStates[placeholderKey] = {
              url: actualUrl,
              scale,
              offsetX: 0,
              offsetY: 0,
              width: imageConfig.width,
              height: imageConfig.height,
              naturalWidth: img.width,
              naturalHeight: img.height,
              isMirrored
            }
          }

        }

        photoStatesRef.current = newPhotoStates
        setRenderKey((prev) => prev + 1)

        // Fire-and-forget: process background removal asynchronously for bgRemoved placeholders.
        // This doesn't block readiness -- the render falls through to the normal image until done.
        for (let idx = 0; idx < frameConfig.drawParams.images.length; idx++) {
          const imgCfg = frameConfig.drawParams.images[idx]
          if (!imgCfg.type.startsWith('photo-') || !imgCfg.bgRemoved) continue
          const selIdx = photoTypeToSelectedIndex[imgCfg.type]
          if (selIdx === undefined || !selectedPhotos[selIdx]) continue
          const url = selectedPhotos[selIdx]
          const raw = url?.startsWith('mirror:') ? url.slice(7) : url
          if (!raw) continue
          const key = placeholderIndexToKey(idx)
          if (bgRemovedImagesCache.current.has(key)) continue
          removeBackground(raw)
            .then((bgImg) => {
              bgRemovedImagesCache.current.set(key, bgImg)
              setRenderKey((prev) => prev + 1)
            })
            .catch((err) => {
              console.warn('[PrintPreview] Background removal failed for', key, err)
            })
        }
      }

      loadImages()
    }, [selectedPhotos, frameConfig.drawParams.images])

    // Monitor loading completion
    useEffect(() => {
      const isComplete = checkLoadingComplete()
      if (isComplete && isLoading) {
        setIsLoading(false)
        isLoadingRef.current = false
      } else if (!isComplete && !isLoading) {
        setIsLoading(true)
        isLoadingRef.current = true
      }
    }, [checkLoadingComplete, isLoading])

    // Shared drawing logic: can target display canvas or offscreen canvas for export
    const drawToContext = useCallback((ctx: CanvasRenderingContext2D): void => {
      const width = ctx.canvas.width
      const height = ctx.canvas.height
      ctx.fillStyle = frameConfig.drawParams.backgroundColor || '#FFFFFF'
      ctx.fillRect(0, 0, width, height)

      for (let i = 0; i < frameConfig.drawParams.images.length; i++) {
        const imageConfig = frameConfig.drawParams.images[i]
        if (imageConfig.type === 'image' && imageConfig.src) {
          const img = staticImages.get(imageConfig.src)
          if (img) {
            ctx.save()
            // Apply rotation if specified
            if (imageConfig.rotation) {
              const centerX = imageConfig.left + imageConfig.width / 2
              const centerY = imageConfig.top + imageConfig.height / 2
              ctx.translate(centerX, centerY)
              ctx.rotate(imageConfig.rotation)
              ctx.translate(-centerX, -centerY)
            }
            ctx.drawImage(
              img,
              imageConfig.left,
              imageConfig.top,
              imageConfig.width,
              imageConfig.height
            )
            ctx.restore()
          }
        } else if (imageConfig.type === 'qr' && qrImage) {
          ctx.save()
          // Apply rotation if specified
          if (imageConfig.rotation) {
            const centerX = imageConfig.left + imageConfig.width / 2
            const centerY = imageConfig.top + imageConfig.height / 2
            ctx.translate(centerX, centerY)
            ctx.rotate(imageConfig.rotation)
            ctx.translate(-centerX, -centerY)
          }
          ctx.drawImage(
            qrImage,
            imageConfig.left,
            imageConfig.top,
            imageConfig.width,
            imageConfig.height
          )
          ctx.restore()
        } else if (imageConfig.type.startsWith('photo-')) {
          const placeholderKey = placeholderIndexToKey(i)
          const photoState = photoStatesRef.current[placeholderKey]
          if (photoState) {
            // Prefer bg-removed image when available, fall through to normal image otherwise
            const img = (imageConfig.bgRemoved && bgRemovedImagesCache.current.get(placeholderKey))
              || photoImagesCache.current.get(photoState.url)
            if (img) {
              ctx.save()

              const centerX = imageConfig.left + imageConfig.width / 2
              const centerY = imageConfig.top + imageConfig.height / 2

              // Move to the center of the placeholder
              ctx.translate(centerX, centerY)

              // Apply the placeholder's rotation
              if (imageConfig.rotation) {
                ctx.rotate(imageConfig.rotation)
              }

              const slotMeta = (imageConfig as { slotMeta?: RawSlotMeta }).slotMeta
              clipPlaceholderAperture(
                ctx,
                imageConfig.width,
                imageConfig.height,
                slotMeta
              )

              // Apply rotation-aware mirroring (aperture fixed; flips photo only)
              if (photoState.isMirrored) {
                const frameRotation = imageConfig.rotation
                  ? (imageConfig.rotation * 180) / Math.PI
                  : 0
                const normalizedRotation = ((frameRotation % 360) + 360) % 360
                switch (normalizedRotation) {
                  case 0:
                  case 180:
                    ctx.scale(-1, 1)
                    break
                  case 90:
                  case 270:
                    ctx.scale(1, -1)
                    break
                }
              }

              // Calculate scaled dimensions of the photo content using the user's scale
              const scaledWidth = photoState.naturalWidth * photoState.scale
              const scaledHeight = photoState.naturalHeight * photoState.scale

              // Calculate draw position relative to the center of the placeholder
              // offsetX and offsetY from photoState are already relative to the center
              const drawX = -scaledWidth / 2 + photoState.offsetX
              const drawY = -scaledHeight / 2 + photoState.offsetY

              // Draw the image content
              ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight)
              ctx.restore()
            }
          } else {
            // Draw placeholder (empty slot) — same aperture/CSE as photos
            ctx.save()
            const isBgRemovedPlaceholder = imageConfig.bgRemoved === true
            const cx = imageConfig.left + imageConfig.width / 2
            const cy = imageConfig.top + imageConfig.height / 2
            ctx.translate(cx, cy)
            if (imageConfig.rotation) {
              ctx.rotate(imageConfig.rotation)
            }
            const slotMetaPh = (imageConfig as { slotMeta?: RawSlotMeta }).slotMeta
            clipPlaceholderAperture(
              ctx,
              imageConfig.width,
              imageConfig.height,
              slotMetaPh
            )
            ctx.fillStyle = isBgRemovedPlaceholder ? 'rgba(229, 231, 235, 0.7)' : '#E5E5E5'
            ctx.fillRect(
              -imageConfig.width / 2,
              -imageConfig.height / 2,
              imageConfig.width,
              imageConfig.height
            )

            if (isBgRemovedPlaceholder) {
              const size = Math.min(imageConfig.width, imageConfig.height) * 0.35
              ctx.fillStyle = 'rgba(107, 114, 128, 0.5)'
              ctx.beginPath()
              ctx.arc(0, -size * 0.3, size * 0.35, 0, Math.PI * 2)
              ctx.fill()
              ctx.beginPath()
              ctx.ellipse(0, size * 0.4, size * 0.6, size * 0.7, 0, 0, Math.PI * 2)
              ctx.fill()
            }

            ctx.fillStyle = '#9CA3AF'
            ctx.font = 'bold 140px Inter'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            const itemNumber = imageConfig.type.split('-')[1]
            ctx.fillText(itemNumber, 0, 0)
            ctx.restore()
          }
        }
      }
    }, [frameConfig.drawParams, staticImages, qrImage, renderKey])

    // Render function (targets display canvas)
    const render = useCallback(async (): Promise<void> => {
      const canvas = canvasRef.current
      const ctx = canvas?.getContext('2d', { willReadFrequently: true })
      if (!canvas || !ctx) return
      canvas.width = frameConfig.drawParams.canvasWidth
      canvas.height = frameConfig.drawParams.canvasHeight
      drawToContext(ctx)
    }, [frameConfig.drawParams.canvasWidth, frameConfig.drawParams.canvasHeight, drawToContext])

    // Render only when necessary
    useEffect(() => {
      render()
    }, [render])

    const updatePhotoState = (placeholderKey: string, updates: Partial<PhotoState>): void => {
      const currentState = photoStatesRef.current[placeholderKey]
      
      // Extract placeholder index from unique key (placeholder-${index})
      const placeholderIndex = parseInt(placeholderKey.replace('placeholder-', ''), 10)
      if (isNaN(placeholderIndex) || placeholderIndex < 0 || placeholderIndex >= frameConfig.drawParams.images.length) {
        return
      }
      const frameImage = frameConfig.drawParams.images[placeholderIndex]

      if (currentState && frameImage) {
        // More lenient zoom constraints
        const newScale = updates.scale
          ? Math.max(0.1, Math.min(5, updates.scale))
          : currentState.scale

        // Calculate scaled dimensions
        const scaledWidth = currentState.naturalWidth * newScale
        const scaledHeight = currentState.naturalHeight * newScale

        // More flexible offset calculations
        let newOffsetX = updates.offsetX !== undefined ? updates.offsetX : currentState.offsetX
        let newOffsetY = updates.offsetY !== undefined ? updates.offsetY : currentState.offsetY

        // Allow more overpan for better user experience
        const maxOverpanFactor = 1.0
        const maxOffsetX = ((scaledWidth - frameImage.width) / 2) * (1 + maxOverpanFactor)
        const maxOffsetY = ((scaledHeight - frameImage.height) / 2) * (1 + maxOverpanFactor)

        newOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, newOffsetX))
        newOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, newOffsetY))

        // Combine updates with defaults
        const newState = {
          ...currentState,
          scale: newScale,
          offsetX: newOffsetX,
          offsetY: newOffsetY,
          ...updates
        }

        photoStatesRef.current[placeholderKey] = newState
        setRenderKey((prev) => prev + 1)
      }
    }

    const updatePhotoStateRef = useRef(updatePhotoState)
    findPhotoAtPositionRef.current = findPhotoAtPosition
    updatePhotoStateRef.current = updatePhotoState

    // Wheel zoom: must use non-passive listener — React's onWheel is passive, so preventDefault() is ignored/warns.
    useEffect(() => {
      const canvas = canvasRef.current
      if (!canvas) return

      const onWheel = (e: WheelEvent): void => {
        if (!isInteractive) return
        e.preventDefault()

        const rect = canvas.getBoundingClientRect()
        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height

        const x = (e.clientX - rect.left) * scaleX
        const y = (e.clientY - rect.top) * scaleY

        const placeholderKey = findPhotoAtPositionRef.current(x, y)

        if (placeholderKey) {
          const photo = photoStatesRef.current[placeholderKey]
          if (photo) {
            const ZOOM_SENSITIVITY = 0.1
            const zoomFactor = -e.deltaY * ZOOM_SENSITIVITY
            const newScale = Math.max(0.1, Math.min(5, photo.scale * (1 + zoomFactor)))

            const scaledWidth = photo.naturalWidth * newScale
            const scaledHeight = photo.naturalHeight * newScale

            const offsetX = photo.offsetX + (scaledWidth - photo.naturalWidth * photo.scale) / 2
            const offsetY = photo.offsetY + (scaledHeight - photo.naturalHeight * photo.scale) / 2

            updatePhotoStateRef.current(placeholderKey, { scale: newScale, offsetX, offsetY })
          }
        }
      }

      canvas.addEventListener('wheel', onWheel, { passive: false })
      return (): void => {
        canvas.removeEventListener('wheel', onWheel)
      }
    }, [isInteractive])

    // Prevent default touch gestures
    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      const preventDefaultGesture = (e: TouchEvent): void => {
        // Only prevent if we have 2 touches (pinch)
        if (e.touches.length === 2) {
          e.preventDefault()
        }
      }

      // Add event listeners with { passive: false } to allow preventDefault
      container.addEventListener('touchstart', preventDefaultGesture, { passive: false })
      container.addEventListener('touchmove', preventDefaultGesture, { passive: false })

      // Also add wheel event listener with { passive: false }
      const preventDefaultWheel = (e: WheelEvent): void => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault()
        }
      }
      container.addEventListener('wheel', preventDefaultWheel, { passive: false })

      return (): void => {
        container.removeEventListener('touchstart', preventDefaultGesture)
        container.removeEventListener('touchmove', preventDefaultGesture)
        container.removeEventListener('wheel', preventDefaultWheel)
      }
    }, [])

    // Wait for ready function (used internally and exposed via ref)
    // Uses isLoadingRef (not isLoading state) to avoid stale closure in polling loop
    const waitForReady = (timeoutMs?: number): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        if (!isLoadingRef.current) {
          resolve()
          return
        }
        
        const timeout = timeoutMs ?? 8000 // Default 8 seconds
        const startTime = Date.now()
        
        const checkReady = (): void => {
          if (!isLoadingRef.current) {
            resolve()
          } else if (Date.now() - startTime >= timeout) {
            reject(new Error('PrintPreview ready timeout'))
          } else {
            setTimeout(checkReady, 100)
          }
        }
        checkReady()
      })

    // Pre-export asset validation: verify all inputs are present before rendering/exporting
    const validateExportAssets = (): { valid: boolean; reason?: string } => {
      if (!frameConfig.drawParams.images.length) {
        return { valid: false, reason: 'empty_frame_config' }
      }
      for (let i = 0; i < frameConfig.drawParams.images.length; i++) {
        const imageConfig = frameConfig.drawParams.images[i]
        if (imageConfig.type.startsWith('photo-')) {
          const placeholderKey = placeholderIndexToKey(i)
          const photoState = photoStatesRef.current[placeholderKey]
          if (!photoState) {
            return { valid: false, reason: `missing_photo_state:${placeholderKey}` }
          }
          if (photoState.loadFailed) {
            return { valid: false, reason: `photo_load_failed:${placeholderKey}` }
          }
          if (!photoImagesCache.current.get(photoState.url)) {
            return { valid: false, reason: `missing_photo_asset:${placeholderKey}` }
          }
        }
        if (imageConfig.type === 'image' && imageConfig.src && !staticImages.has(imageConfig.src)) {
          return { valid: false, reason: `missing_frame_overlay:${imageConfig.src.slice(0, 40)}` }
        }
      }
      return { valid: true }
    }

    /**
     * Core export: renders to offscreen canvas and returns blob.
     * strict=true (default): throws on waitForReady timeout or validation failure.
     * strict=false: waits best-effort, skips validation, renders whatever is loaded.
     */
    const exportImageWithStatus = async (
      opts?: { timeoutMs?: number; strict?: boolean }
    ): Promise<ExportResult> => {
      const totalTimeout = opts?.timeoutMs ?? 8000
      const strict = opts?.strict ?? true
      const startTime = Date.now()

      const photoSlotCount = frameConfig.drawParams.images.filter((i) => i.type.startsWith('photo-')).length
      const staticImageCount = frameConfig.drawParams.images.filter((i) => i.type === 'image' && i.src).length
      console.info('[PrintPreview] Export started', {
        sessionId,
        strict,
        photoSlotCount,
        staticImageCount,
        canvasWidth: frameConfig.drawParams.canvasWidth,
        canvasHeight: frameConfig.drawParams.canvasHeight
      })

      // Wait for assets to load
      try {
        await waitForReady(totalTimeout)
      } catch (error) {
        if (strict) throw error
        console.warn('[PrintPreview] waitForReady timed out, proceeding with degraded export', {
          sessionId,
          elapsed: Date.now() - startTime
        })
      }

      // Validate assets
      const validation = validateExportAssets()
      let degradedReason: string | undefined
      if (!validation.valid) {
        if (strict) {
          console.error('[PrintPreview] Pre-export validation failed', { sessionId, reason: validation.reason })
          throw new Error(`PrintPreview export validation failed: ${validation.reason ?? 'unknown'}`)
        }
        degradedReason = validation.reason
        console.warn('[PrintPreview] Degraded export: missing assets', { sessionId, reason: validation.reason })
      }

      const w = frameConfig.drawParams.canvasWidth
      const h = frameConfig.drawParams.canvasHeight
      const offscreenCanvas = document.createElement('canvas')
      offscreenCanvas.width = w
      offscreenCanvas.height = h
      const offscreenCtx = offscreenCanvas.getContext('2d', { willReadFrequently: false })
      if (!offscreenCtx) {
        throw new Error('Failed to get export canvas context')
      }
      drawToContext(offscreenCtx)

      const blob = await new Promise<Blob>((resolve, reject) => {
        // Budget for toBlob is pixel-based and intentionally independent of totalTimeout.
        // IMPORTANT: callers can no longer use timeoutMs to bound the blob encoding step.
        // timeoutMs only controls how long waitForReady waits for assets.
        // Large canvases (e.g. 3200×4800 at 2× print scale) can take 5–20 s to JPEG-encode
        // on budget photobooth hardware. The old formula (totalTimeout * 0.3, capped at 3 s)
        // was always too tight for high-res frames.
        const pixelCount = w * h
        const blobTimeoutMs = Math.max(
          10000,                                          // minimum 10 s floor
          Math.min(45000, Math.ceil(pixelCount / 400000) * 1000) // ~1 s per 400 k pixels
        )
        console.debug('[PrintPreview] toBlob budget', { pixelCount, blobTimeoutMs, w, h })
        const blobTimeout = setTimeout(() => {
          reject(new Error('Export blob creation timeout'))
        }, blobTimeoutMs)

        offscreenCanvas.toBlob(
          (result) => {
            clearTimeout(blobTimeout)
            if (result) {
              resolve(result)
            } else {
              reject(new Error('Failed to create blob'))
            }
          },
          'image/jpeg',
          0.95
        )
      })

      const duration = Date.now() - startTime
      console.info('[PrintPreview] Export result', {
        sessionId,
        blobSize: blob.size,
        durationMs: duration,
        assetsVerified: !degradedReason,
        degradedReason
      })

      return { blob, assetsVerified: !degradedReason, degradedReason }
    }

    // Legacy wrapper used by many custom routes.
    // Keep compatibility by applying the same resilient strategy used in newer Select pages:
    // strict attempt -> strict retry (extended timeout) -> degraded fallback.
    const exportImage = async (timeoutMs?: number): Promise<Blob> => {
      const strictTimeout = timeoutMs ?? 8000
      try {
        const strictResult = await exportImageWithStatus({ timeoutMs: strictTimeout, strict: true })
        return strictResult.blob
      } catch (firstError) {
        const firstMessage = firstError instanceof Error ? firstError.message : 'unknown'
        console.warn('[PrintPreview] Legacy export strict attempt failed, retrying', {
          sessionId,
          reason: firstMessage
        })
      }

      try {
        const retryTimeout = Math.max(strictTimeout, 15000)
        const retryResult = await exportImageWithStatus({ timeoutMs: retryTimeout, strict: true })
        return retryResult.blob
      } catch (retryError) {
        const retryMessage = retryError instanceof Error ? retryError.message : 'unknown'
        console.warn('[PrintPreview] Legacy export retry failed, using degraded fallback', {
          sessionId,
          reason: retryMessage
        })
      }

      // Degraded fallback: short waitForReady (3 s) then encode whatever is loaded.
      // blobTimeoutMs is pixel-based and independent of timeoutMs, so encoding still gets up to
      // 45 s for large canvases. The short timeoutMs here is intentional — assets haven't loaded
      // after two prior attempts; proceed with whatever is available.
      const fallbackResult = await exportImageWithStatus({ timeoutMs: 3000, strict: false })
      return fallbackResult.blob
    }

    // Transform unique-keyed states back to photoType-keyed format for backward compatibility
    const getPhotoStatesByType = (): PhotoStateMap => {
      const result: PhotoStateMap = {}
      
      // Iterate through all placeholders and group by photoType
      for (let i = 0; i < frameConfig.drawParams.images.length; i++) {
        const imageConfig = frameConfig.drawParams.images[i]
        if (imageConfig.type.startsWith('photo-')) {
          const placeholderKey = placeholderIndexToKey(i)
          const photoState = photoStatesRef.current[placeholderKey]
          
          if (photoState) {
            // If multiple placeholders share the same type, use the first one found
            // This maintains backward compatibility with video processing
            if (!result[imageConfig.type]) {
              result[imageConfig.type] = photoState
            }
          }
        }
      }
      
      return result
    }

    useImperativeHandle(ref, () => ({
      exportImage,
      exportImageWithStatus,
      getPhotoStates: getPhotoStatesByType,
      isReady: (): boolean => !isLoading,
      waitForReady
    }))

    return (
      <div
        ref={containerRef}
        className={className}
        style={{
          touchAction: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none'
        }}
      >
        <canvas
          ref={canvasRef}
          data-preview-type="print"
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            cursor: 'grab'
          }}
        />
      </div>
    )
  }
)

PrintPreview.displayName = 'PrintPreview'

export default PrintPreview
